import axios, { AxiosRequestConfig } from "axios";
import { API_URL } from "../config/config";
import { getHeaderOptions } from "./getHeaderOptions";
import { FileWithProgress, LogLevel } from "../views/content/types";
import logger, { Logger } from '../utils/logger';

const API_ENDPOINT = API_URL + "api/v1";

// Types from FileUploadHandler that we want to keep
interface FileProgress {
  fileName: string;
  progress: number;
  uploaded: boolean;
  error?: string;
}

interface UploadOptions {
  maxSize?: number; // in MB
  onProgress?: (progress: FileProgress) => void;
  abortSignal?: AbortSignal;
}

interface DeleteFileRequest {
  filePaths: string[];
  type: string;
  content: string;
}

// Constants
const DEFAULT_MAX_SIZE = 100; // MB
const UPLOAD_QUEUE: Array<() => Promise<any>> = [];
let isProcessingQueue = false;

// Logger utility
const log = (message: string, level: LogLevel = LogLevel.INFO) => {
  Logger.log(`[UtilityService] [${level}] ${message}`);
};

// Validation function inspired by FileUploadHandler
const validateFileSize = (file: File, maxSize: number): string | null => {
  log(`Validating file size: ${file.name}`, LogLevel.DEBUG);
  const sizeMB = file.size / (1024 * 1024);
  return sizeMB > maxSize ? `File size exceeds ${maxSize}MB limit` : null;
};

// Queue processor
const processQueue = async () => {
  if (isProcessingQueue || UPLOAD_QUEUE.length === 0) return;
  
  isProcessingQueue = true;
  log(`Processing queue: ${UPLOAD_QUEUE.length} items`, LogLevel.INFO);
  
  try {
    let result;
    while (UPLOAD_QUEUE.length > 0) {
      const uploadTask = UPLOAD_QUEUE.shift();
      if (uploadTask) {
        result = await uploadTask(); // Store the result
      }
    }
    return result; // Return the last task's result
  } catch (error) {
    log(`Queue processing error: ${error}`, LogLevel.ERROR);
    throw error; // Propagate error up
  } finally {
    isProcessingQueue = false;
  }
};

// Enhanced uploadFiles function - keeping the same signature but improving internals
export const uploadFiles = async (
  formData: FormData, 
  options: UploadOptions = {}
): Promise<any> => {
  const {
    maxSize = DEFAULT_MAX_SIZE,
    onProgress,
    abortSignal
  } = options;

  // Validate all files first
  const files = Array.from(formData.getAll('files') as File[]);
  log(`Preparing to upload ${files.length} files`, LogLevel.INFO);
  
  for (const file of files) {
    const error = validateFileSize(file, maxSize);
    if (error) {
      log(error, LogLevel.ERROR);
      throw new Error(error);
    }
  }

  // Create upload task with proper response handling
  const uploadTask = () => new Promise((resolve, reject) => {
    const config: AxiosRequestConfig = {
      headers: {
        ...getHeaderOptions,
        Authorization: localStorage.getItem("auth_token") || "",
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          files.forEach(file => {
            onProgress({
              fileName: file.name,
              progress: percent,
              uploaded: percent === 100
            });
          });
        }
      },
      signal: abortSignal
    };

    log(`Starting upload for ${files.length} files`, LogLevel.INFO);

    axios
      .post(`${API_ENDPOINT}/utility/file-upload`, formData, config)
      .then((response) => {
        log('Upload successful', LogLevel.INFO);
        Logger.log("RAW API RESPONSE:", JSON.stringify(response.data));
        // Convert response to a standardized format
        const result = {
          status: response.status,
          statusCode: response.status,
          data: response.data.data || response.data,
          message: response.data.message || 'Files uploaded successfully'
        };
        Logger.log("TRANSFORMED RESPONSE:", JSON.stringify(result));
        
        resolve(result);
      })
      .catch((error) => {
        logger.error("UPLOAD API ERROR:", error);
        const err = error as Error;
    log(`Upload failed: ${err.message || String(err)}`, LogLevel.ERROR);
    reject({
      status: false,
      statusCode: (err as any).response?.status || 500,
      message: (err as any).response?.data?.message || err.message || 'Upload failed',
      error: err
    });
      });
  });
  Logger.log("ADDING TO UPLOAD QUEUE");
  // Add to queue and process
  UPLOAD_QUEUE.push(uploadTask);
  return processQueue();
};

// Enhanced deleteLeadFile function
export const deleteLeadFile = async (payload: DeleteFileRequest): Promise<any> => {
  log(`Deleting files: ${payload.filePaths.join(', ')}`, LogLevel.INFO);
  
  try {
    const response = await axios.put(
      `${API_ENDPOINT}/utility/delete-file`,
      payload,
      {
        headers: {
          ...getHeaderOptions,
          Authorization: localStorage.getItem("auth_token") || "",
        }
      }
    );

    log('Deletion successful', LogLevel.INFO);
    return response;
  } catch (exception) {
    const error = exception as Error;
    const errorMessage = (error as any).response?.data?.message || error.message || 'Deletion failed';
    log(`Deletion failed: ${errorMessage}`, LogLevel.ERROR);
    throw error;
  }
};

// Cleanup utility
export const cleanupUploads = (): void => {
  log('Cleaning up upload queue', LogLevel.INFO);
  UPLOAD_QUEUE.length = 0;
  isProcessingQueue = false;
};

// ============================================
// 🔥 NEW: REUSABLE SIGNED URL UPLOAD SYSTEM
// ============================================

// Supported file upload types
export type FileUploadType = 
  | "lead" 
  | "content" 
  | "product" 
  | "deal" 
  | "activity" 
  | "user" 
  | "digital_card" 
  | "vcf_file" 
  | "quotation" 
  | "lead_note" 
  | "order" 
  | "call_record" 
  | "invoice" 
  | "organization_icon_url" 
  | "task";

// Signed URL request interface
interface SignedUrlRequest {
  recordId: string;
  fileName: string;
  callRecordingDate?: string;
  callRecordingUnixDate?: string;
  fileId?: string;
  fieldName?: string;
  type: FileUploadType;
  mimeType: string;
}

// Signed URL response interface
interface SignedUrlResponse {
  status: boolean;
  data: {
    signedUrl: string;
    fileUrl: string;
    filePath: string;
    fileName: string;
    fieldName?: string;
  };
  message: string;
}

// File upload progress interface
interface FileUploadProgress {
  fileName: string;
  progress: number;
  uploaded: boolean;
  error?: string;
}

// File upload options for signed URL
interface SignedUploadOptions {
  onProgress?: (progress: FileUploadProgress) => void;
  abortSignal?: AbortSignal;
}

/**
 * Get signed URL for file upload
 * @param request - Signed URL request parameters
 * @returns Promise with signed URL response
 */
export const getSignedUrl = async (request: SignedUrlRequest): Promise<SignedUrlResponse> => {
  log(`🔗 [SIGNED URL] Requesting signed URL for ${request.fileName} (${request.type})`, LogLevel.INFO);
  
  const url = `${API_ENDPOINT}/utility/sign-url`;
  const auth_token = localStorage.getItem("auth_token") || "";
  
  log(`🔗 [SIGNED URL] API Endpoint: ${url}`, LogLevel.INFO);
  log(`🔗 [SIGNED URL] Request params:`, LogLevel.INFO);
  logger.log('[SIGNED URL] Request object:', request);
  log(`🔗 [SIGNED URL] Auth token exists: ${!!auth_token}`, LogLevel.INFO);
  
  try {
    const response = await axios.get(
      url,
      {
        params: request,
        headers: {
          ...getHeaderOptions,
          Authorization: auth_token,
          "token-check-disable": "true"
        }
      }
    );

    log(`🔗 [SIGNED URL] Response status: ${response.status}`, LogLevel.INFO);
    logger.log('[SIGNED URL] Full response:', response.data);

    if (response.data.status) {
      log(`Signed URL obtained successfully for ${request.fileName}`, LogLevel.INFO);
      return response.data;
    } else {
      throw new Error(response.data.message || 'Failed to get signed URL');
    }
  } catch (error) {
    const err = error as Error;
    log(`Failed to get signed URL: ${err.message}`, LogLevel.ERROR);
    throw error;
  }
};

/**
 * Upload file to S3 using signed URL
 * @param file - File to upload
 * @param signedUrl - Signed URL from getSignedUrl()
 * @param options - Upload options
 * @returns Promise with upload result
 */
export const uploadToSignedUrl = async (
  file: File, 
  signedUrl: string, 
  options: SignedUploadOptions = {}
): Promise<any> => {
  const { onProgress, abortSignal } = options;
  
  log(`Uploading ${file.name} to signed URL`, LogLevel.INFO);

  try {
    const response = await axios.put(signedUrl, file, {
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress({
            fileName: file.name,
            progress: percent,
            uploaded: percent === 100
          });
        }
      },
      signal: abortSignal
    });

    log(`Upload successful for ${file.name}`, LogLevel.INFO);
    return {
      status: true,
      statusCode: response.status,
      message: 'File uploaded successfully',
      data: response.data
    };
  } catch (error) {
    const err = error as Error;
    log(`Upload failed for ${file.name}: ${err.message}`, LogLevel.ERROR);
    
    if (onProgress) {
      onProgress({
        fileName: file.name,
        progress: 0,
        uploaded: false,
        error: err.message
      });
    }
    
    throw error;
  }
};

/**
 * Complete signed URL upload process (get signed URL + upload file)
 * @param file - File to upload
 * @param uploadConfig - Upload configuration
 * @param options - Upload options
 * @returns Promise with complete upload result
 */
// REMOVED DUPLICATE - Using uploadFilesWithSignedUrl instead

// REMOVED DUPLICATE - Using uploadFilesWithSignedUrl instead


export const uploadProductImages = async (
  files: File[],
  productId: string,
  options: UploadOptions = {}
): Promise<any> => {
  if (files.length === 0) {
    log('No files to upload for product', LogLevel.INFO);
    return { success: false, message: 'No files provided' };
  }
  
  log(`Preparing to upload ${files.length} image(s) for product ID: ${productId} using signed URLs`, LogLevel.INFO);

  try {
    // Use the new signed URL upload method
    const result = await uploadFilesWithSignedUrl(files, productId, 'product', options);

    // Return in compatible format for existing code
    return {
      status: result.status,
      statusCode: result.statusCode,
      data: result.data,
      successful: result.successful,
      failed: result.failed,
      message: result.message
    };
  } catch (error: any) {
    log(`Product image upload failed: ${error.message}`, LogLevel.ERROR);
    return {
      status: false,
      statusCode: 500,
      data: [],
      successful: [],
      failed: files.map(file => ({
        fileName: file.name,
        error: error.message || 'Upload failed'
      })),
      message: error.message || 'Product image upload failed'
    };
  }
};

/**
 * Upload files using signed URL API
 * @param files - Array of files to upload
 * @param recordId - ID of the record (leadId, contentId, etc.)
 * @param type - Type of upload ('lead', 'content', 'product', etc.)
 * @param options - Upload options
 */
export const uploadFilesWithSignedUrl = async (
  files: File[],
  recordId: string,
  type: string,
  options: UploadOptions = {}
): Promise<any> => {
  try {
    const { onProgress, abortSignal } = options;

    log(`Starting signed URL upload for ${files.length} files, type: ${type}, recordId: ${recordId}`, LogLevel.INFO);

    const successful: any[] = [];
    const failed: any[] = [];

    for (const file of files) {
      try {
        // Step 1: Get signed URL
        const signedUrlResponse = await axios.get(
          `${API_ENDPOINT}/utility/sign-url`,
          {
            headers: {
              ...getHeaderOptions,
              Authorization: localStorage.getItem('auth_token') || '',
            },
            params: {
              recordId: recordId,
              fileName: file.name,
              type: type,
              mimeType: file.type
            }
          }
        );

        // Check if we got the signed URL (based on your API response structure)
        if (!signedUrlResponse.data.data || !signedUrlResponse.data.data.signedUrl) {
          throw new Error('Failed to get signed URL from response');
        }

        const { signedUrl, fileUrl } = signedUrlResponse.data.data;

        // Step 2: Upload file to signed URL
        await axios.put(signedUrl, file, {
          headers: {
            'Content-Type': file.type,
          },
          onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total) {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress({
                fileName: file.name,
                progress: percent,
                uploaded: percent === 100
              });
            }
          },
          signal: abortSignal
        });

        successful.push({
          fileName: file.name,
          fileUrl: fileUrl,
          fileId: fileUrl, // Use fileUrl as fileId since your API doesn't provide separate fileId
          filePath: fileUrl
        });

        log(`Successfully uploaded ${file.name}`, LogLevel.INFO);

      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Upload failed';
        failed.push({
          fileName: file.name,
          error: errorMessage
        });
        log(`Failed to upload ${file.name}: ${errorMessage}`, LogLevel.ERROR);
      }
    }

    return {
      status: failed.length === 0,
      statusCode: failed.length === 0 ? 200 : 207,
      data: successful,
      successful: successful,
      failed: failed,
      message: failed.length === 0
        ? `Successfully uploaded ${successful.length} files`
        : `Uploaded ${successful.length}/${files.length} files. ${failed.length} failed.`
    };

  } catch (error: any) {
    log(`Signed URL upload failed: ${error.message}`, LogLevel.ERROR);
    return {
      status: false,
      statusCode: 500,
      data: [],
      successful: [],
      failed: files.map(file => ({
        fileName: file.name,
        error: error.message || 'Upload failed'
      })),
      message: error.message || 'Upload failed'
    };
  }
};